import api_data, { getQueryString } from '@/Api/api_data';
import Api from '@/Api/api_simulaion';
import templateData from '@/component/template/tools/template';
import Tools from '@/component/template/tools/tools';
import { CardData } from '@/component/LibraryOfEquipmentInformation/EquipmentInformationContent/interface';
import { cloneDeep, concat } from 'lodash';

export default class ProjectExportTool {
    constructor() {
        this.simuId = getQueryString()['simu_id'];
    }
    simuId: string;

    taskId = '';

    /**
     * 设备列表
     * 带挡位的设备
     */
    equipmentListWithTable = ['燃气轮机', '热泵', '燃气锅炉', '电压缩制冷机', '吸收式制冷机', '离心泵'];

    /**
     * 设备列表
     */
    equipmentList = [
        {
            name: '光伏',
            type: 'dpcs',
            kind: 'PhotovoltaicSys',
        },
        {
            name: '风机',
            type: 'dpcs',
            kind: 'WindPowerGenerator',
        },
        {
            name: '燃气轮机',
            type: 'dpcs',
            kind: 'GasTurbine',
        },
        {
            name: '热泵',
            type: 'dhscs',
            kind: 'HeatPump',
        },
        {
            name: '燃气锅炉',
            type: 'dhscs',
            kind: 'GasBoiler',
        },
        {
            name: '热管式太阳能集热器',
            type: 'dhscs',
            kind: 'HPSolarCollector',
        },
        {
            name: '电压缩制冷机',
            type: 'dhscs',
            kind: 'CompRefrg',
        },
        {
            name: '吸收式制冷机',
            type: 'dhscs',
            kind: 'AbsorptionChiller',
        },
        {
            name: '蓄电池',
            type: 'escs',
            kind: 'Battery',
        },
        {
            name: '储水罐',
            type: 'escs',
            kind: 'WaterTank',
        },
        {
            name: '变压器',
            type: 'dstcs',
            kind: 'Transformer',
        },
        {
            name: '传输线',
            type: 'dstcs',
            kind: 'TransferLine',
        },
        {
            name: '模块化多电平变流器',
            type: 'dstcs',
            kind: 'MMC',
        },
        {
            name: '离心泵',
            type: 'hstcs',
            kind: 'CentrifugalPump',
        },
        {
            name: '管道',
            type: 'hstcs',
            kind: 'Pipe',
        },
    ];

    /**
     *  获得方案列表数据
     *  数据处理
     *  2022-8-29 更新，原因：数据结构变化
     */
    async loadPlanListData(): Promise<Array<Array<string | number>>> {
        const result = new Array<Array<string | number>>();
        const responseOfLastSimulationTask = await Api.getLastSimulationTask({ simuid: this.simuId });
        if (responseOfLastSimulationTask.error !== 0 || responseOfLastSimulationTask.data == null) {
            throw new Error('暂不存在完整的方案数据');
        }
        this.taskId = responseOfLastSimulationTask.data.task_id;

        const responseOfOptimizationResult = await Api.getOptimizationResult({ resultType: 0, simuid: this.simuId, taskid: this.taskId });
        if (responseOfOptimizationResult.data.length === 0) {
            throw new Error('暂不存在完整的方案数据');
        }
        const header = responseOfOptimizationResult.data[0].data.headerDesc;
        const column = header.map((x) => {
            return x.hearderName;
        });
        responseOfOptimizationResult.data.forEach((item) => {
            const obj = item.data.data[0];
            const arr = Object.keys(obj).map((x) => {
                const name = header.find((item) => item.key === x)?.hearderName;
                return {
                    name: name,
                    value: obj[x],
                };
            });
            const data = [] as (number | string)[];
            for (let i of column) {
                const d = arr.find((x) => x.name === i);
                if (!d) {
                    return;
                }
                data.push(d.value);
            }
            result.push(data);
        });
        result.unshift(column);
        return result;
    }

    // 获取方案配置
    async loadPlanDetailInfo(planId: number): Promise<Array<Array<string | number>>> {
        const result = new Array<Array<string | number>>();
        const responseOfPlanDetailInfo = await Api.getPlanDetailInfo(this.simuId, this.taskId, planId);
        //获取表头描述列表
        const headerDescList = responseOfPlanDetailInfo.data[0].data.headerDesc;
        result[0] = headerDescList.map((item) => item.hearderName);
        responseOfPlanDetailInfo.data.forEach((item) => {
            const row = new Array<string | number>();
            headerDescList.forEach((headerDesc) => {
                row.push(item.data.data[headerDesc.key]);
            });
            result.push(row);
        });
        return result;
    }

    // 获取设备信息列表
    async loadEquipmentInformationDetailsData() {
        const promiseListOfEquipmentInformationData = this.equipmentList.map(async (list) => {
            let result = new Array<Array<string | number>>();
            if (this.equipmentListWithTable.includes(list.name)) {
                const ls = await api_data.getDataByParams({ type: list.type, params: list.kind });
                const tHeader = templateData[list.kind].map((x: { name: string }) => {
                    return x.name;
                });
                const keys = templateData[list.kind].map((x: { key: string }) => {
                    return x.key;
                });
                result.push(tHeader);
                ls.results.forEach((item) => {
                    const data = this.handleData(item as CardData, keys);
                    result = concat(result, data);
                });
                return { name: list.name, data: result };
            } else {
                const equipmentInformationData = await api_data.getDataByParams({ type: list.type, params: list.kind });
                equipmentInformationData.results.forEach((item, index) => {
                    const data = Tools.initXlsxData(list.name, list.name, item);
                    if (index < 1) {
                        result.push(data.tHeader);
                    }
                    result.push(data.data[0]);
                });
            }
            return { name: list.name, data: result };
        });
        return await Promise.all(promiseListOfEquipmentInformationData);
    }

    /**
     * 数据处理
     */
    handleData(item: CardData, keys: string[]): string[][] {
        const r = Object.keys(item.ratedParam);
        const t = (item.kind === 'CentrifugalPump' ? item.ratedParam.PerformanceCurve : item.ratedParam.OperateParam) as Record<string, string>[];
        const o = Object.keys(item.operationalConstraints);
        const e = Object.keys(item.economicParam);
        const d = [item.manufacturer, item.equipType];
        const str = '';
        let result = new Array<Array<string>>();
        t.forEach((_x, i) => {
            if (i === 0) {
                result[0] = concat([], d);
            } else {
                result[i] = concat([], ['', '']);
            }
        });
        keys.forEach((x) => {
            t.forEach((v, i) => {
                if (i === 0) {
                    if (r.length > 0) {
                        r.forEach((y) => {
                            if (x === y) {
                                result[0].push(item.ratedParam[x] as string);
                            }
                        });
                    }
                    if (Object.keys(v).length > 0) {
                        Object.keys(v).forEach((y) => {
                            if (x === y) {
                                result[0].push(v[x]);
                            }
                        });
                    }
                    if (o.length > 0) {
                        o.forEach((y) => {
                            if (x === y) {
                                result[0].push(item.operationalConstraints[x] as string);
                            }
                        });
                    }
                    if (e.length > 0) {
                        e.forEach((y) => {
                            if (x === y) {
                                result[0].push(item.economicParam[x] as string);
                            }
                        });
                    }
                } else {
                    if (r.length > 0) {
                        r.forEach((y) => {
                            if (x === y) {
                                result[i].push(str);
                            }
                        });
                    }
                    if (Object.keys(v).length > 0) {
                        Object.keys(v).forEach((y) => {
                            if (x === y) {
                                result[i].push(v[x]);
                            }
                        });
                    }
                    if (o.length > 0) {
                        o.forEach((y) => {
                            if (x === y) {
                                result[i].push(str);
                            }
                        });
                    }
                    if (e.length > 0) {
                        e.forEach((y) => {
                            if (x === y) {
                                result[i].push(str);
                            }
                        });
                    }
                }
            });
        });
        return result;
    }

    // 导出配置方案和设备信息excel
    async exportToFiles() {
        const planList = await this.loadPlanListData();
        const promiseOfPlanDetailInfoList = new Array(planList.length - 1).fill('').map(async (item, index) => {
            return { name: `方案${index + 1}`, data: await this.loadPlanDetailInfo(index) };
        });
        const planDetailInfoList = await Promise.all(promiseOfPlanDetailInfoList);
        await Tools.exportXlsxFile('项目文件', [{ name: '方案列表', data: planList }, ...planDetailInfoList]);
        const equipmentInformationDataList = await this.loadEquipmentInformationDetailsData();
        await Tools.exportXlsxFile('设备信息库', equipmentInformationDataList);
    }
}
